import PropTypes from 'prop-types';
import AccountType from 'scripts/constants/AccountType';
import IdentityCheck from 'scripts/constants/IdentityCheck';
import RegistrationStep from 'scripts/constants/RegistrationStep';
import styled from 'styled-components';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import { purple50, purple700, yellow100 } from 'src/themes/brickx/colors';
import { useClientDetail } from './ClientDetailContext';
import {
  formatCount,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatPlanType,
  formatStatus,
  getAccountTypeTone,
  getStatusTone,
} from './helpers';
import {
  AccountTypeBadge,
  Card,
  CardHeader,
  CardTitle,
  DataTabLayout,
  DataTable,
  DataTableScroll,
  DetailDescription,
  DetailList,
  DetailRow,
  DetailTerm,
  EmptyCopy,
  StatusPill,
} from './styles';
import { DetailRows } from './components';

const formatBoolean = (value) => {
  if (value === null || value === undefined) return '—';
  return value ? 'Yes' : 'No';
};

const formatOnboarding = (value) => {
  const step = String(value || '').toLowerCase();
  if (!step) return null;
  if (step === RegistrationStep.DONE) return 'Completed';
  if (step === RegistrationStep.VEDA) return 'ID Check';
  if ([RegistrationStep.PDS, RegistrationStep.TMD].includes(step)) {
    return step.toUpperCase();
  }
  return formatStatus(step);
};

const getOnboardingTone = (value, identityCheck) => {
  const step = String(value || '').toLowerCase();
  if ([RegistrationStep.SIGNUP, RegistrationStep.VERIFY].includes(step)) {
    return 'warning';
  }
  if (step === RegistrationStep.DONE) return 'positive';
  if ([RegistrationStep.DEPOSIT, RegistrationStep.INVEST].includes(step)) {
    return 'funding';
  }
  if (
    step === RegistrationStep.VEDA &&
    String(identityCheck || '').toLowerCase() === IdentityCheck.FAILED
  ) {
    return 'negative';
  }
  return 'info';
};

const RegistrationStepBadge = styled(StatusPill)`
  &.warning {
    background: ${yellow100};
    color: #765b00;
  }

  &.funding {
    background: ${purple50};
    color: ${purple700};
  }
`;

const AccountTopGrid = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
  gap: ${gridUnit * 4}px;
  align-items: stretch;
`;

const PlansTable = styled(DataTable)`
  min-width: 980px;
`;

const BusinessOwnersTable = styled(DataTable)`
  th,
  td {
    white-space: normal;
  }

  td:nth-child(3) {
    min-width: 180px;
    overflow-wrap: anywhere;
  }
`;

const AccountLayout = styled(DataTabLayout)`
  ${CardHeader} {
    min-height: 72px;
  }

  ${CardTitle} {
    font-size: 20px;
    font-weight: 500;
    line-height: 1.2;
  }

  ${DetailTerm}, ${DetailDescription}, ${EmptyCopy} {
    color: var(--bx-color---grey-800);
    font-size: 16px;
    line-height: 1.4;
  }

  ${DetailTerm} {
    font-weight: 400;
  }

  ${DetailDescription} {
    font-weight: 500;
  }

  ${DetailRow} {
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    gap: ${gridUnit * 3}px;
  }

  ${AccountTopGrid} > ${Card} {
    display: flex;
    flex-direction: column;
  }

  ${AccountTopGrid} ${DetailList} {
    grid-auto-rows: minmax(28px, auto);
    align-content: start;
  }

  ${AccountTopGrid} ${DetailRow} {
    align-items: center;
  }

  ${AccountTopGrid} ${DetailRow}.personal-address {
    align-items: start;
  }

  ${AccountTopGrid} ${DetailDescription} {
    display: flex;
    align-items: center;
  }

  ${StatusPill} {
    min-height: 24px;
    margin: 0;
    padding: ${gridUnit / 2}px ${gridUnit * 2}px;
    border-radius: 3px;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.4;
  }

  ${DataTableScroll} {
    width: calc(100% - 2 * var(--advisor-card-gutter));
    max-width: calc(100% - 2 * var(--advisor-card-gutter));
    margin: 0 var(--advisor-card-gutter) ${gridUnit * 6}px;
    border-top: 0;
  }

  ${PlansTable}, ${BusinessOwnersTable} {
    min-width: 720px;
    color: var(--bx-color---grey-800);
    font-size: 16px;
    line-height: 1.4;

    th,
    td {
      height: 51px;
      box-sizing: border-box;
      padding: 0 ${gridUnit * 3}px;
      border-bottom-color: var(--bx-color---grey-100);
      color: var(--bx-color---grey-800);
      font-size: 16px;
      font-weight: 400;
    }

    th:first-child,
    td:first-child {
      padding-left: 0;
    }

    th:last-child,
    td:last-child {
      padding-right: 0;
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }
  }
`;

const PersonalInformationCard = ({ identity }) => {
  const rows = [
    { label: 'Full Name', value: identity.name },
    { label: 'Email', value: identity.email },
    { label: 'Mobile', value: identity.mobile },
    { label: 'Date of Birth', value: formatDate(identity.dateOfBirth) },
    { label: 'Age', value: formatCount(identity.age) },
    {
      label: 'Address',
      value: identity.address,
      className: 'personal-address',
    },
  ].filter(({ value }) => value && value !== '—');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Personal Details</CardTitle>
      </CardHeader>
      <DetailRows rows={rows} />
    </Card>
  );
};

PersonalInformationCard.propTypes = {
  identity: PropTypes.object.isRequired,
};

const AccountInformationCard = ({ detail }) => {
  const { compliance, identity } = detail;
  const rows = [
    {
      label: 'Account Type',
      content: identity.accountTypeLabel ? (
        <AccountTypeBadge className={getAccountTypeTone(identity.accountType)}>
          {identity.accountTypeLabel}
        </AccountTypeBadge>
      ) : null,
    },
    {
      label: 'Registration Step',
      content: compliance.onboarding ? (
        <RegistrationStepBadge
          className={getOnboardingTone(
            compliance.onboarding,
            compliance.identityCheck,
          )}
        >
          {formatOnboarding(compliance.onboarding)}
        </RegistrationStepBadge>
      ) : null,
    },
    {
      label: 'Account Status',
      content: compliance.accountStatus ? (
        <StatusPill className={getStatusTone(compliance.accountStatus)}>
          {formatStatus(compliance.accountStatus)}
        </StatusPill>
      ) : null,
    },
    {
      label: 'Tax Residency',
      value: formatStatus(compliance.australianTaxResidency),
    },
    { label: 'Client Since', value: formatDate(identity.clientSince) },
    { label: 'Last Login', value: formatDateTime(identity.lastLogin) },
  ].filter(({ value, content }) => (value && value !== '—') || content);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account Details</CardTitle>
      </CardHeader>
      <DetailRows rows={rows} />
    </Card>
  );
};

AccountInformationCard.propTypes = {
  detail: PropTypes.object.isRequired,
};

const EntityDetailsCard = ({ detail }) => {
  const { account, identity, specialAccounts } = detail;
  const rows = [
    { label: 'Entity Type', value: identity.accountTypeLabel },
    { label: 'Legal Name', value: account.legalName },
  ];
  const { smsf, minor, wholesale } = specialAccounts;

  if (smsf) {
    const entityAddress = [smsf.address, smsf.suburb, smsf.state, smsf.postcode]
      .filter(Boolean)
      .join(', ');
    rows.push(
      { label: 'Entity Name', value: smsf.entityName },
      { label: 'Trustee Type', value: formatStatus(smsf.trusteeType) },
      { label: 'ABN', value: smsf.abn },
      { label: 'ACN', value: smsf.acn },
      { label: 'Entity Address', value: entityAddress || null },
      { label: 'Corporate Trustee', value: smsf.corporateTrusteeName },
      {
        label: 'Trustee Address',
        value: smsf.corporateTrusteeAddress,
      },
      {
        label: 'Director of Trustee',
        value: formatBoolean(smsf.directorOfCorporateTrustee),
      },
    );
  }

  if (minor) {
    rows.push(
      { label: 'Trust Name', value: minor.trustName },
      {
        label: 'Minor Name',
        value: [minor.givenName, minor.middleName, minor.familyName]
          .filter(Boolean)
          .join(' '),
      },
      { label: 'Minor Date of Birth', value: formatDate(minor.dateOfBirth) },
      { label: 'Relationship', value: formatStatus(minor.relationship) },
    );
  }

  if (wholesale) {
    rows.push(
      {
        label: 'Wholesale Verified',
        value: formatBoolean(wholesale.verified),
      },
      {
        label: 'Certificate Available',
        value: formatBoolean(wholesale.certificateAvailable),
      },
      {
        label: 'Certificate Expiry',
        value: formatDate(wholesale.certificateExpiry),
      },
    );
  }

  const visibleRows = rows.filter(
    ({ value }) => value !== null && value !== undefined && value !== '',
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Entity Details</CardTitle>
      </CardHeader>
      <DetailRows rows={visibleRows} />
    </Card>
  );
};

EntityDetailsCard.propTypes = {
  detail: PropTypes.object.isRequired,
};

const BusinessOwnersCard = ({ detail }) => {
  const { beneficialOwners } = detail.specialAccounts;
  if (
    getAccountTypeTone(detail.identity.accountType) !== 'business' ||
    !beneficialOwners.length
  ) {
    return null;
  }

  const accountType = String(detail.identity.accountType || '').toUpperCase();
  const title =
    {
      [AccountType.SMSF]: 'Beneficiaries Details',
      [AccountType.TRUST]: 'Beneficiaries Details',
      [AccountType.CORPORATION]: 'Shareholders Details',
      COMPANY: 'Shareholders Details',
    }[accountType] || 'Beneficial Owners';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <DataTableScroll>
        <BusinessOwnersTable aria-label={title}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Date of Birth</th>
              <th>Address</th>
              <th>ID Checked</th>
              <th>Roles</th>
            </tr>
          </thead>
          <tbody>
            {beneficialOwners.map((owner, index) => {
              const roles = [
                owner.trustee && 'Trustee',
                owner.director && 'Director',
                owner.shareholder && 'Shareholder',
                owner.beneficiary && 'Beneficiary',
              ]
                .filter(Boolean)
                .join(', ');
              return (
                <tr key={`${owner.name || 'owner'}-${index}`}>
                  <td>{owner.name || '—'}</td>
                  <td>{formatDate(owner.dateOfBirth)}</td>
                  <td>{owner.address || '—'}</td>
                  <td>{formatBoolean(owner.identityChecked)}</td>
                  <td>{roles || 'None recorded'}</td>
                </tr>
              );
            })}
          </tbody>
        </BusinessOwnersTable>
      </DataTableScroll>
    </Card>
  );
};

BusinessOwnersCard.propTypes = {
  detail: PropTypes.object.isRequired,
};

const InvestmentPlansCard = ({ plans }) => (
  <Card>
    <CardHeader>
      <CardTitle>Investment Plans</CardTitle>
    </CardHeader>
    {plans.length ? (
      <DataTableScroll>
        <PlansTable>
          <thead>
            <tr>
              <th>Plan</th>
              <th>Start Date</th>
              <th className="numeric">Portfolio Value</th>
              <th className="numeric">Amount Invested</th>
              <th className="numeric">Available Balance</th>
              <th className="numeric">Account Value</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan, index) => (
              <tr key={plan.id || `${plan.type}-${index}`}>
                <td>{formatPlanType(plan.type)}</td>
                <td>{formatDate(plan.createdAt)}</td>
                <td className="numeric">
                  {formatCurrency(plan.portfolioValue)}
                </td>
                <td className="numeric">
                  {formatCurrency(plan.amountInvested)}
                </td>
                <td className="numeric">
                  {formatCurrency(plan.availableBalance)}
                </td>
                <td className="numeric">
                  {formatCurrency(plan.totalAccountValue)}
                </td>
              </tr>
            ))}
          </tbody>
        </PlansTable>
      </DataTableScroll>
    ) : (
      <EmptyCopy>No investment plans are available.</EmptyCopy>
    )}
  </Card>
);

InvestmentPlansCard.propTypes = {
  plans: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const AccountTab = () => {
  const { detail } = useClientDetail();
  return (
    <AccountLayout>
      <AccountTopGrid>
        <PersonalInformationCard identity={detail.identity} />
        <AccountInformationCard detail={detail} />
      </AccountTopGrid>
      {['business', 'minor'].includes(
        getAccountTypeTone(detail.identity.accountType),
      ) && <EntityDetailsCard detail={detail} />}
      <BusinessOwnersCard detail={detail} />
      <InvestmentPlansCard plans={detail.plans} />
    </AccountLayout>
  );
};

export default AccountTab;
